import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  EmailSchema,
  VerifyCodeBodySchema,
  type MeResponse,
  type VerifyCodeBody,
} from '../../../src/shared/auth';
import { authKeys, fetchMe, requestCode, verifyCode } from '../../api/auth';
import { TurnstileWidget } from '../../components/TurnstileWidget';
import { ErrorNotice } from '../../components/ErrorNotice';
import { HealthBadge } from '../../components/HealthBadge';

/**
 * `/login` — email OTP sign-in (ARCHITECTURE_PLAN §6.1, §10). Two steps:
 *   1. email + Turnstile token → `POST /api/auth/request-code` → 6-digit code email;
 *   2. code → `POST /api/auth/verify-code` → session cookie (HttpOnly, Set-Cookie
 *      on the response), then bootstrap `GET /api/auth/me` to seed the React
 *      Query cache and route: back to the page a guard bounced us from, else the
 *      first workspace, else `/onboarding` for users with no memberships.
 *
 * The Turnstile token is single-use, so the widget lives on the email step;
 * going back ("use a different email" / resend) remounts it for a fresh
 * challenge. Server failures render the envelope's safe message + requestId.
 */

/** The public site key is build-time client config; read lazily so tests can stub env. */
function turnstileSiteKey(): string {
  return import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';
}

/** Read the path a route guard bounced us from, if any (`location.state.from`). */
function readFromLocationState(state: unknown): string | null {
  if (typeof state === 'object' && state !== null && 'from' in state) {
    const from: unknown = state.from;
    if (typeof from === 'string' && from.startsWith('/')) {
      return from;
    }
  }
  return null;
}

export function LoginPage(): React.JSX.Element {
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [emailFieldError, setEmailFieldError] = useState<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const from = readFromLocationState(location.state);

  const requestCodeMutation = useMutation({
    mutationFn: requestCode,
    onSuccess: () => {
      setStep('code');
    },
  });

  const verifyMutation = useMutation({
    mutationFn: async (body: VerifyCodeBody): Promise<MeResponse> => {
      // Session cookie is set by the verify response; bootstrap immediately so
      // the cache is seeded and we can route on real membership data.
      await verifyCode(body);
      return fetchMe();
    },
    onSuccess: (me) => {
      queryClient.setQueryData(authKeys.me, me);
      const first = me.memberships[0];
      const target = from ?? (first !== undefined ? `/w/${first.slug}` : '/onboarding');
      void navigate(target, { replace: true });
    },
  });

  const handleEmailSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = EmailSchema.safeParse(email);
    if (!parsed.success) {
      setEmailFieldError(parsed.error.issues[0]?.message ?? 'Enter a valid email address.');
      return;
    }
    setEmailFieldError(null);
    if (turnstileToken === null) {
      // The button is disabled until the challenge completes; this guards
      // against programmatic submits. §6.1 requires the token server-side.
      return;
    }
    requestCodeMutation.mutate({ email: parsed.data, turnstileToken });
  };

  return (
    <main className="flex min-h-screen items-center justify-center px-6">
      <div className="w-full max-w-md">
        <h1 className="text-3xl font-semibold tracking-tight">Astralis FrontDesk</h1>
        <p className="mt-2 text-slate-600">Multi-tenant AI front desk and lead operating system.</p>

        <section className="mt-8 rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          {step === 'email' ? (
            <>
              <h2 className="text-lg font-medium">Sign in</h2>
              <p className="mt-1 text-sm text-slate-600">
                We email you a one-time sign-in code. No password needed.
              </p>
              <form className="mt-4 space-y-4" onSubmit={handleEmailSubmit} noValidate>
                <div>
                  <label htmlFor="login-email" className="block text-sm font-medium text-slate-700">
                    Email
                  </label>
                  <input
                    id="login-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none"
                  />
                  {emailFieldError !== null && (
                    <p className="mt-1 text-sm text-red-600">{emailFieldError}</p>
                  )}
                </div>

                <TurnstileWidget siteKey={turnstileSiteKey()} onToken={setTurnstileToken} />

                {requestCodeMutation.isError && <ErrorNotice error={requestCodeMutation.error} />}

                <button
                  type="submit"
                  disabled={turnstileToken === null || requestCodeMutation.isPending}
                  className="w-full rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {requestCodeMutation.isPending ? 'Sending code…' : 'Send sign-in code'}
                </button>
              </form>
            </>
          ) : (
            <CodeStep
              email={email}
              isPending={verifyMutation.isPending}
              error={verifyMutation.isError ? verifyMutation.error : null}
              onSubmit={(code) => verifyMutation.mutate({ email, code })}
              onUseDifferentEmail={() => {
                // The Turnstile token is single-use; returning to the email
                // step remounts the widget for a fresh challenge (resend path).
                verifyMutation.reset();
                setTurnstileToken(null);
                setStep('email');
              }}
            />
          )}
        </section>

        <section className="mt-6 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-sm font-medium text-slate-700">Backend status</h2>
          <div className="mt-2">
            <HealthBadge />
          </div>
        </section>
      </div>
    </main>
  );
}

/** Step 2: enter the 6-digit code emailed in step 1. */
function CodeStep({
  email,
  isPending,
  error,
  onSubmit,
  onUseDifferentEmail,
}: {
  email: string;
  isPending: boolean;
  error: unknown;
  onSubmit: (code: string) => void;
  onUseDifferentEmail: () => void;
}): React.JSX.Element {
  const [code, setCode] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = VerifyCodeBodySchema.safeParse({ email, code });
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message ?? 'Enter the 6-digit code.');
      return;
    }
    setFieldError(null);
    onSubmit(parsed.data.code);
  };

  return (
    <>
      <h2 className="text-lg font-medium">Enter your code</h2>
      <p className="mt-1 text-sm text-slate-600">
        We sent a 6-digit code to <span className="font-medium text-slate-900">{email}</span>. It
        expires in 10 minutes.
      </p>
      <form className="mt-4 space-y-4" onSubmit={handleSubmit} noValidate>
        <div>
          <label htmlFor="login-code" className="block text-sm font-medium text-slate-700">
            6-digit code
          </label>
          <input
            id="login-code"
            name="code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            required
            maxLength={6}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-lg tracking-[0.5em] shadow-sm focus:border-slate-500 focus:outline-none"
          />
          {fieldError !== null && <p className="mt-1 text-sm text-red-600">{fieldError}</p>}
        </div>

        {error !== null && <ErrorNotice error={error} />}

        <button
          type="submit"
          disabled={code.length !== 6 || isPending}
          className="w-full rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isPending ? 'Verifying…' : 'Verify and sign in'}
        </button>

        <button
          type="button"
          onClick={onUseDifferentEmail}
          className="w-full text-sm font-medium text-slate-600 underline underline-offset-4 hover:text-slate-900"
        >
          Use a different email or resend the code
        </button>
      </form>
    </>
  );
}
